import angular from 'angular';
import angularRoute from 'angular-route';
import booksMenu from '../../menu/Menu';
import addChangeItem from '../../com/AddChangeItem/AddChangeItem';
import addChangeTagDialog from '../../com/AddChangeTagDialog/AddChangeTagDialog';
import addChangeItemCategoryDialog from '../../com/AddChangeItemCategoryDialog/AddChangeItemCategoryDialog';
import { itemAdminServiceProvider } from '../../services/ItemAdminService';
import itemAdminTemplate from './itemAdmin.template.html';
import './itemAdmin.css';
/** Item Categories */
import { ItemCategoriesController } from './dom/itemCategories/itemCategories';
/** Item Options */
import { ItemOptionsController } from './dom/itemOptions/itemOptions';
/** Add Change Item Option Master Dialog */
import {
  AddChangeItemOptionMasterDialogService,
  optionMasterValueInputManagerComponent
} from './dom/itemOptions/AddChangeItemOptionMasterDialog/AddChangeItemOptionMasterDialog';
/** Items */
import { GenerateBarcodeSheetDialogService } from './dom/items/generateBarcodeSheetDialog';
import { ItemsController } from './dom/items/items';
import { PrintItemListDialogService } from './dom/items/printItemListDialog';

export default angular
  .module('csm.books.ItemAdmin', [
    angularRoute,
    'ui.grid',
    'ui.grid.saveState',
    'ui.grid.resizeColumns',
    booksMenu,
    addChangeItem,
    addChangeTagDialog,
    addChangeItemCategoryDialog
  ])
  .factory('ItemTypes', itemTypesFactory)
  .factory('DiscountTypes', discountTypesFactory)
  .factory('ItemAdminGridStates', itemAdminGridStatesFactory)
  .factory('OptionMasterTypes', optionMasterTypesFactory)
  .filter('mapOptionValues', mapOptionValuesFilter)
  .filter('mapOptionTypes', mapOptionTypesFilter)
  .directive('itemAdmin', itemAdminDirective)
  .provider('ItemAdminService', itemAdminServiceProvider)
  .controller('itemCategoriesController', ItemCategoriesController)
  .controller('itemOptionsController', ItemOptionsController)
  .service(
    'AddChangeItemOptionMasterDialog',
    AddChangeItemOptionMasterDialogService
  )
  .component(
    'optionMasterValueInputManager',
    optionMasterValueInputManagerComponent
  )
  .service('GenerateBarcodeSheetDialog', GenerateBarcodeSheetDialogService)
  .controller('itemsController', ItemsController)
  .service('PrintItemListDialog', PrintItemListDialogService).name;

/*******************************************************************************
 * Factory: ItemTypes */
/**
 * @ngdoc    factory
 * @name     ItemTypes
 * @memberof csm.books.itemAdmin
 *
 * @description
 * This is a factory that returns item types and their corresponding ids as an
 * array of objects. In order to inject SERVER, this had to be a factory
 * as opposed to an angular constant which does not allow for dependency
 * injection.
 *******************************************************************************/
/* @ngInject */ function itemTypesFactory(SERVER) {
  /** Item Type Descriptions */
  var types = [
    { value: SERVER.BooksDBConstants.IT_EQUIPMENT, label: 'Equipment' },
    { value: SERVER.BooksDBConstants.IT_TEXTBOOKS, label: 'Textbook' },
    { value: SERVER.BooksDBConstants.IT_RECORDS, label: 'Record' },
    { value: SERVER.BooksDBConstants.IT_FEES, label: 'Fee' },
    { value: SERVER.BooksDBConstants.IT_MEMBERSHIP, label: 'Membership' },
    { value: SERVER.BooksDBConstants.IT_FINES, label: 'Fine' },
    { value: SERVER.BooksDBConstants.IT_PRODUCT, label: 'Product' },
    { value: SERVER.BooksDBConstants.IT_DONATION, label: 'Donation' },
    { value: SERVER.BooksDBConstants.IT_GROUP, label: 'Kit' }
  ];
  return types;
}

/*******************************************************************************
* Factory: DiscountTypes
/**
* @ngdoc    factory
* @name     DiscountTypes
* @memberof csm.ui.itemAdmin
*
* List of discount types. Used in the AddChangeDiscountDialog and in the 
* discounts tab of the AddChangeItemDialog. A shared list is needed so that 
* we know if all discounts of all types have been added in the discounts tab.
*******************************************************************************/
/* @ngInject */ function discountTypesFactory(SERVER) {
  return [
    {
      value: SERVER.BooksDBConstants.DT_MEMBERSHIP,
      label: SERVER.productID == 'ASB Works' ? 'ASB Card Holder' : 'Membership'
    },
    { value: SERVER.BooksDBConstants.DT_STUDENT, label: 'Student' },
    { value: SERVER.BooksDBConstants.DT_TEACHER, label: 'Teacher' }
  ];
}

/*******************************************************************************
* Factory: ItemAdminGridStates
/**
* @ngdoc    factory
* @name     ItemAdminGridStates
* @memberof csm.ui.itemAdmin
*
* Contains information about the current grids used with ItemAdmin
*******************************************************************************/
/* @ngInject */ function itemAdminGridStatesFactory(GridState) {
  return {
    categories: new GridState(),
    items: new GridState(),
    options: new GridState()
  };
}

/*******************************************************************************
 * Factory: OptionMasterTypes */
/**
 * @ngdoc    factory
 * @name     OptionMasterTypes
 * @memberof csm.ui.itemAdmin
 *
 * @description
 * This is a factory that returns item option types and their corresponding ids
 * as an array of objects. In order to inject SERVER, this had to be a
 * factory as opposed to an angular constant which does not allow for
 * dependency injection.
 *******************************************************************************/
/* @ngInject */ function optionMasterTypesFactory(SERVER) {
  /** Item Option Master types*/
  var types = [
    { value: '', label: 'All Types' },
    {
      value: SERVER.BooksDBConstants.MOT_SELECTION_LIST,
      label: 'Selection List'
    },
    { value: SERVER.BooksDBConstants.MOT_TEXT_INPUT, label: 'Text Input' },
    { value: SERVER.BooksDBConstants.MOT_GUEST_NAME, label: 'Guest Name' }
  ];

  /*------------------------------------------------------------------*/
  /* Only ASB Works should include the Guest Name option in the list. */
  /*------------------------------------------------------------------*/
  if (SERVER.productID != 'ASB Works')
    types = types.filter(function (t) {
      return t.value != SERVER.BooksDBConstants.MOT_GUEST_NAME;
    });

  return types;
}

/*******************************************************************************
 * Filter: mapOptionValues */
/**
 * @ngdoc    filter
 * @name     mapOptionValues
 * @memberof csm.ui.itemAdmin
 *
 * @description
 * Used in the values column of the Options grid to properly display options as
 * a comma separated string.
 *******************************************************************************/
/* @ngInject */ function mapOptionValuesFilter() {
  return function (values) {
    return values ? values.join(', ') : '';
  };
}

/*******************************************************************************
 * Filter: mapOptionTypes */
/**
 * @ngdoc    factory
 * @name     mapOptionTypes
 * @memberof csm.ui.itemAdmin
 *
 * @description
 * Used in the type column of the Options grid. Returns the string to be
 * displayed for the given type constant.
 *******************************************************************************/
/* @ngInject */ function mapOptionTypesFilter(OptionMasterTypes) {
  var itemTypeHash = {};
  angular.forEach(OptionMasterTypes, function (itemType) {
    itemTypeHash[itemType.value] = itemType.label;
  });

  return function (input) {
    return !input ? '' : itemTypeHash[input];
  };
}

/*******************************************************************************
 * Item Admin Directive
 *
 *
 *
 * @ngdoc     directive
 * @memberof  books.ui
 * @name      books.ui.itemAdmin
 *******************************************************************************/

/* @ngInject */ function itemAdminDirective(
  $location,
  $q,
  SERVER,
  BooksSvc,
  BusyScreen
) {
  /** Directive controller */
  var itemAdminController = function ($scope) {
    /*---------------------------*/
    /* Route navigation options. */
    /*---------------------------*/
    var mItems = { display: 'Items', active: true, route: '/' };
    var mItemCategories = {
      display: 'Item Categories',
      active: false,
      route: '/categories/'
    };
    var mOptions = {
      display: 'Options',
      active: false,
      route: '/options/'
    };

    $scope.options = [mItems, mItemCategories, mOptions];

    /***************************************************************************
     * List Item functions
     ***************************************************************************/
    /** Donation Flag */ $scope.isDonation = function (item) {
      return item.type == SERVER.BooksDBConstants.IT_DONATION;
    };
    /** Fees type flag */ $scope.isFees = function (item) {
      return item.type == SERVER.BooksDBConstants.IT_FEES;
    };
    /** Group type flag */ $scope.isGroup = function (item) {
      return item.type == SERVER.BooksDBConstants.IT_GROUP;
    };
    /** Inventory Product Flag */ $scope.isInvProduct = function (item) {
      return $scope.isProduct(item) && item.trackInventory;
    };
    /** Membership type flag */ $scope.isMembership = function (item) {
      return item.type == SERVER.BooksDBConstants.IT_MEMBERSHIP;
    };
    /** Permit type flag */ $scope.isPermit = function (item) {
      return item.type == SERVER.BooksDBConstants.IT_PERMITS;
    };
    /** Product type flag */ $scope.isProduct = function (item) {
      return item.type == SERVER.BooksDBConstants.IT_PRODUCT;
    };
    /** Record type flag */ $scope.isRecord = function (item) {
      return item.type == SERVER.BooksDBConstants.IT_RECORDS;
    };

    /***************************************************************************
     * onOption */
    /**
     * Nav buttons click handler. Clears the former active option and navigates to
     * the route for the passed option.
     *
     * @param   option  Option to make active
     ***************************************************************************/
    $scope.onOption = function (option) {
      /*----------------------------------------*/
      /* Ignore if the option is already active */
      /*----------------------------------------*/
      if (option.active) return;

      /*------------------------------------------*/
      /* Set active flag on user selection and    */
      /* disable on the other navigation buttons. */
      /*------------------------------------------*/
      if ($scope.options)
        $scope.options.forEach(function (c) {
          c.active = false;
        });

      option.active = true;

      /*--------------------*/
      /* Navigate to route. */
      /*--------------------*/
      $location.path(option.route);
    };

    /*-------------------------------------------------------------*/
    /* Highlight the appropriate option based on the current route */
    /*-------------------------------------------------------------*/
    $scope.onOption(
      $scope.options
        .filter(function (option) {
          return option.route == $location.path();
        })
        .shift()
    );
  };

  /*****************************************************************************
   * Directive: itemAdmin
   *
   * This is where all item control information is managed. The setting up of
   * available items, options, and categories are configured within this app.
   *
   * scope:
   * @attr {object}   appFlags            Array of application flags to check
   * @attr {Boolean}  hideItemSku         True=hide SKU column in itemsGrid.
   *                                      Passed through to AddChangeItemDialog
   * @attr {Boolean}  anonDefaultValue    True=allow anonymous sale. Passed
   *                                      through to AddChangeItemDialog
   * @attr {Boolean}  hideIncidental      True=hide the incidental checkbox.
   *                                      Passed through to AddChangeItemDialog.
   *                                      Shown by default.
   * @attr {Boolean}  hideOptionQtyChange True=hide the qty change column in
   *                                      options tab grid. Passed through to
   *                                      AddChangeItemDialog. Shown by default.
   *
   *****************************************************************************/
  return {
    restrict: 'E',
    controller: itemAdminController,
    template: itemAdminTemplate,
    scope: {
      appFlags: '=',
      hideItemSku: '=',
      anonDefaultValue: '=',
      hideIncidental: '=',
      hideOptionQtyChange: '='
    }
  };
}
